import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Save, 
  Edit3, 
  Clock, 
  Plus, 
  Trash2, 
  Upload, 
  X,
  List
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_POINTS_PER_ITEM = 8;

const FIXED_ITEMS = [
  { 
    key: 'motto', 
    label: 'School Motto', 
    defaultShortTitle: 'Non Scholae Sed Vitae Discimus',
    defaultTitle: 'Non Scholae Sed Vitae Discimus',
    defaultDesc: 'The true purpose of education extends beyond academic achievement or the completion of schooling it is to cultivate the knowledge, skills, and character essential for meaningful success in life.',
    defaultQuote: 'Non Scholae Sed Vitae Discimus'
  },
  { 
    key: 'philosophy', 
    label: 'Philosophy', 
    defaultShortTitle: 'Faith, Fortitude, Fulfilment',
    defaultTitle: 'Faith, Fortitude, and Fulfilment',
    defaultDesc: 'Together, Faith, Fortitude, and Fulfilment reflect the holistic qualities that Permata Harapan Secondary seeks to develop in every student.',
    defaultQuote: "Together, Faith, Fortitude, and Fulfilment reflect the holistic qualities that Permata Harapan Secondary seeks to develop in every student. These core values are expressed through a set of guiding dispositions that shape students' character, mindset, and actions."
  },
  { 
    key: 'logo', 
    label: 'Logo Philosophy', 
    defaultShortTitle: 'Five Elements of Excellence',
    defaultTitle: 'Logo Philosophy',
    defaultDesc: 'Every component of our crest tells the story of our educational values and student aspirations.',
    defaultQuote: ''
  }
];

export default function AboutUsPhilosophyForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    subtitle: '',
    items: []
  });

  const [pendingFields, setPendingFields] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [modalItemData, setModalItemData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/aboutus/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AboutUsPhilosophyForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    if (typeof mediaId === 'string' && (mediaId.startsWith('http://') || mediaId.startsWith('https://') || mediaId.startsWith('blob:') || mediaId.startsWith('data:'))) {
      return mediaId;
    }
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    if (mediaMap[String(mediaId)]) return mediaMap[String(mediaId)];
    return resolveMediaSrc(mediaId, mediaMap) || mediaId;
  };

  const loadData = async () => {
    await Promise.all([loadMediaMap(), loadSection()]);
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let philosophySection = null;
      
      const adminResponse = await fetch(
        `${apiBase}/aboutus/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          philosophySection = result.data.find(s => s.section_type === 'about-us-philosophy-shell');
        }
      }
      
      if (!philosophySection) {
        const publicResponse = await fetch(
          `${apiBase}/aboutus/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            philosophySection = result.data.find(s => s.section_type === 'about-us-philosophy-shell');
          }
        }
      }
      
      if (!philosophySection) {
        throw new Error('Philosophy section not found');
      }

      const currentSectionId = philosophySection.id;
      setSectionId(currentSectionId);

      let published = philosophySection.content_published;
      if (typeof published === 'string') {
        try { published = JSON.parse(published); } catch (e) { published = {}; }
      }

      const publishedEn = published?.en || {};
      const rawItems = Array.isArray(publishedEn.items) ? publishedEn.items : [];

      const initialItems = FIXED_ITEMS.map((fix, idx) => {
        const existing = rawItems[idx] || {};
        return {
          key: fix.key,
          label: existing.label || fix.label,
          shortTitle: existing.shortTitle || fix.defaultShortTitle,
          title: existing.title || fix.defaultTitle,
          description: existing.description !== null && existing.description !== undefined ? existing.description : fix.defaultDesc,
          quote: existing.quote || fix.defaultQuote,
          index: existing.index || `0${idx + 1}`,
          image: existing.image || '',
          media_id: existing.media_id || '',
          points: Array.isArray(existing.points) ? existing.points : []
        };
      });

      const initialData = {
        kicker: publishedEn.kicker || 'School Philosophy',
        title: publishedEn.title || 'Our Guiding Principles',
        subtitle: publishedEn.subtitle || publishedEn.intro || 'Discover the core values, philosophy, and symbolic identity that guide Permata Harapan School.',
        items: initialItems
      };

      const pendingMap = {};

      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${currentSectionId}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        const pendingResult = await pendingResponse.json();
        if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
          const { pending_changes } = pendingResult.data;
          if (Array.isArray(pending_changes)) {
            pending_changes.forEach(change => {
              if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                let val = change.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                const itemMatch = change.field_id.match(/^items\.(\d+)(?:\.|$)/);
                if (itemMatch) {
                  const itemIdx = parseInt(itemMatch[1], 10);
                  pendingMap[`item_${itemIdx}`] = true;
                  if (val && initialData.items[itemIdx]) {
                    if (typeof val === 'object' && val !== null) {
                      delete val.update;
                    }
                    initialData.items[itemIdx] = {
                      ...initialData.items[itemIdx],
                      ...val
                    };
                  }
                } else if (change.field_id in initialData) {
                  pendingMap[change.field_id] = true;
                  if (val !== undefined && val !== null) {
                    initialData[change.field_id] = val;
                  }
                }
              }
            });
          }
        }
      }

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingFields(pendingMap);
    } catch (err) {
      console.error('[AboutUsPhilosophyForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleModalItemChange = (field, value) => {
    setModalItemData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleModalPointChange = (pointIndex, field, value) => {
    setModalItemData(prev => ({
      ...prev,
      points: (prev.points || []).map((point, pIdx) =>
        pIdx === pointIndex ? { ...point, [field]: value } : point
      )
    }));
  };

  const handleAddPoint = () => {
    if (!modalItemData) return;
    const currentPoints = modalItemData.points || [];
    if (currentPoints.length >= MAX_POINTS_PER_ITEM) {
      toast.warning(`Maximum ${MAX_POINTS_PER_ITEM} points allowed`);
      return;
    }
    setModalItemData(prev => ({
      ...prev,
      points: [...currentPoints, { title: '', description: '' }]
    }));
  };

  const handleDeletePoint = (pointIndex) => {
    setModalItemData(prev => ({
      ...prev,
      points: (prev.points || []).filter((_, pIdx) => pIdx !== pointIndex)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              const mediaUrl = response.url || response.file_path || response.data?.url || response.data?.file_path;
              if (mediaId) {
                if (mediaUrl) {
                  setMediaMap(prev => ({
                    ...prev,
                    [mediaId]: mediaUrl,
                    [String(mediaId)]: mediaUrl
                  }));
                }
                handleModalItemChange('image', mediaUrl || mediaId);
                handleModalItemChange('media_id', mediaId);
                toast.success('Emblem image uploaded! Click Save below to submit for approval.');
              } else {
                toast.error('No media ID returned');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error(`Upload failed (HTTP ${xhr.status})`);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/aboutus/cms/media/upload?section_type=about-us-philosophy-shell`);
      xhr.withCredentials = true;
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (savedToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${savedToken}`);
      }
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleSubmitField = async (fieldName, label) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: label || fieldName.replace(/_/g, ' '),
            new_value: formData[fieldName],
            old_value: originalData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${label || fieldName} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitItemModal = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const itemIndex = editingItemIndex;
    const item = modalItemData;
    const itemKey = `item_${itemIndex}`;

    if (!item || !item.title) {
      toast.warning('Please enter a title');
      return;
    }

    // Validate points if present
    if (item.points && item.points.length > 0) {
      const hasEmpty = item.points.some(p => !p.title?.trim() || !p.description?.trim());
      if (hasEmpty) {
        toast.warning('All sub-points must have both title and description');
        return;
      }
    }

    setSubmitting(prev => ({ ...prev, [itemKey]: true }));

    try {
      const fixed = FIXED_ITEMS[itemIndex];
      const itemLabel = fixed?.label || `Item ${itemIndex + 1}`;

      const cleanItem = {
        key: fixed?.key || item.key || `item-${itemIndex}`,
        label: fixed?.label || item.label || itemLabel,
        shortTitle: (item.shortTitle || item.title || '').trim(),
        title: (item.title || '').trim(),
        description: (item.description || '').trim(),
        quote: (item.quote || '').trim(),
        index: item.index || `0${itemIndex + 1}`,
        image: item.image || '',
        media_id: item.media_id || '',
        points: Array.isArray(item.points) ? item.points.map(p => ({
          title: (p.title || '').trim(),
          description: (p.description || '').trim()
        })) : []
      };

      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `items.${itemIndex}.update`,
            field_name: `Update ${itemLabel}`,
            old_value: originalData.items[itemIndex] || {},
            new_value: cleanItem,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${itemLabel} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [itemKey]: true }));
        setFormData(prev => ({
          ...prev,
          items: prev.items.map((it, i) => i === itemIndex ? cleanItem : it)
        }));
        setEditingItemIndex(null);
        setModalItemData(null);
      } else {
        if (response.status === 409) {
          toast.error('This item already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [itemKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading School Philosophy...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <BookOpen size={22} />
          </div>
          <div>
            <h2 className="unified-title">School Philosophy, Motto & Symbolism</h2>
            <p className="unified-subtitle">Manage institutional philosophy, school motto, and logo emblem symbolism</p>
          </div>
        </div>
      </div>

      {/* Section Tag / Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Tag / Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="60"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker || submitting.kicker}
            placeholder="e.g. School Philosophy"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker', 'Section Kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Section Headline */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Headline</label>
          {pendingFields.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            maxLength="120"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title || submitting.title}
            placeholder="e.g. Our Guiding Principles"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', 'Section Headline')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Section Subtitle / Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Subtitle / Description</label>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            maxLength="500"
            className="unified-textarea"
            rows="3"
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={pendingFields.subtitle || submitting.subtitle}
            placeholder="e.g. Discover the core values, philosophy, and symbolic identity that guide Permata Harapan School."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('subtitle', 'Section Subtitle')}
            disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} /> {submitting.subtitle ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Items List (Vertical stack) */}
      <div style={{ marginTop: '34px' }}>
        <div style={{ marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
            Philosophy Elements (School Motto, Educational Philosophy & Logo Symbolism)
          </h3>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
            Click edit on any element below to revise its headline, statement quote, detailed narrative, sub-points, and emblem
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {FIXED_ITEMS.map((fixedItem, idx) => {
            const item = formData.items[idx] || {};
            const itemKey = `item_${idx}`;
            const isPending = pendingFields[itemKey];
            const imgSrc = getMediaUrl(item.image);
            const pointsCount = Array.isArray(item.points) ? item.points.length : 0;

            return (
              <div
                key={fixedItem.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '20px',
                  padding: '22px 26px',
                  borderRadius: '14px',
                  border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                  borderLeft: isPending ? '5px solid #f59e0b' : `5px solid ${idx === 0 ? '#2563eb' : idx === 1 ? '#8b5cf6' : '#0d9488'}`,
                  background: isPending ? '#fffdf7' : '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Emblem / Illustration Thumbnail */}
                {imgSrc && (
                  <div
                    style={{
                      width: '90px',
                      height: '90px',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      background: '#0f172a',
                      border: '1px solid #cbd5e1'
                    }}
                  >
                    <img
                      src={imgSrc}
                      alt={fixedItem.label}
                      style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff', cursor: 'pointer' }}
                      onClick={() => setLightboxImage({ src: imgSrc, title: fixedItem.label })}
                    />
                  </div>
                )}

                {/* Info Column */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <span
                      style={{
                        padding: '3px 10px',
                        borderRadius: '6px',
                        background: '#eff6ff',
                        color: '#1d4ed8',
                        fontSize: '11px',
                        fontWeight: '800',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em'
                      }}
                    >
                      {item.index || `0${idx + 1}`} • {fixedItem.label}
                    </span>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                      {item.title || fixedItem.defaultTitle}
                    </h4>
                    {isPending && (
                      <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                        <Clock size={10} /> Pending Review
                      </span>
                    )}
                  </div>

                  {item.quote && (
                    <p style={{ margin: '0 0 8px', fontSize: '13px', fontStyle: 'italic', color: '#6366f1', fontWeight: '600' }}>
                      &ldquo;{item.quote}&rdquo;
                    </p>
                  )}

                  <p style={{ margin: '0 0 10px', fontSize: '13.5px', color: '#475569', lineHeight: 1.5 }}>
                    {item.description || (idx === 1 ? 'Holistic educational values guiding character, mindset, and action.' : idx === 2 ? 'Visual emblem symbolism representing our institution.' : 'Guiding motto shaping learning and life purpose.')}
                  </p>

                  {/* Sub-points preview */}
                  {pointsCount > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <List size={13} /> {pointsCount} Pillar Point{pointsCount > 1 ? 's' : ''}:
                      </span>
                      {item.points.map((pt, pIdx) => (
                        <span
                          key={pIdx}
                          style={{
                            fontSize: '11.5px',
                            background: '#f1f5f9',
                            color: '#334155',
                            padding: '2px 8px',
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                            fontWeight: '600'
                          }}
                        >
                          {pt.title || `Point ${pIdx + 1}`}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action */}
                <button
                  type="button"
                  className="unified-secondary-btn"
                  onClick={() => {
                    if (isPending) {
                      toast.info('This item is currently awaiting approval');
                      return;
                    }
                    setEditingItemIndex(idx);
                    setModalItemData({
                      ...item,
                      points: Array.isArray(item.points) ? [...item.points] : []
                    });
                  }}
                  disabled={isPending}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0, marginTop: '2px' }}
                >
                  <Edit3 size={14} /> Edit Item
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Item Modal */}
      {editingItemIndex !== null && modalItemData && (
        <div className="unified-modal-overlay" onClick={() => {
          setEditingItemIndex(null);
          setModalItemData(null);
        }}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                  Edit {FIXED_ITEMS[editingItemIndex]?.label}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Update headline, quote statement, narrative, points, and emblem
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingItemIndex(null);
                  setModalItemData(null);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              {/* Title Input */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Pillar Headline / Title</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalItemData.title || ''}
                  onChange={(e) => handleModalItemChange('title', e.target.value)}
                  placeholder={`e.g. ${FIXED_ITEMS[editingItemIndex]?.defaultTitle}`}
                />
              </div>

              {/* Short Title / Tab Label */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Pill Tab Label / Short Title</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalItemData.shortTitle || ''}
                  onChange={(e) => handleModalItemChange('shortTitle', e.target.value)}
                  placeholder={`e.g. ${FIXED_ITEMS[editingItemIndex]?.defaultShortTitle}`}
                />
              </div>

              {/* Quote / Motto Statement */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Key Quote / Motto Statement (Optional)</label>
                <textarea
                  className="unified-textarea"
                  rows="2"
                  value={modalItemData.quote || ''}
                  onChange={(e) => handleModalItemChange('quote', e.target.value)}
                  placeholder="e.g. Non Scholae Sed Vitae Discimus or guiding inspirational motto..."
                />
              </div>

              {/* Description Input */}
              <div style={{ marginBottom: '20px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Detailed Narrative / Summary</label>
                <textarea
                  className="unified-textarea"
                  rows="4"
                  value={modalItemData.description || ''}
                  onChange={(e) => handleModalItemChange('description', e.target.value)}
                  placeholder="Explain the background and deeper philosophical significance..."
                />
              </div>

              {/* Sub-points Section */}
              <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <label className="unified-label" style={{ margin: 0 }}>
                    Detailed Points / Symbolic Elements ({(modalItemData.points || []).length} / {MAX_POINTS_PER_ITEM})
                  </label>
                  {(modalItemData.points || []).length < MAX_POINTS_PER_ITEM && (
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={handleAddPoint}
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      <Plus size={13} /> Add Point
                    </button>
                  )}
                </div>

                {(modalItemData.points || []).length === 0 ? (
                  <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
                    No sub-points added for this item. (e.g. Logo parts or Philosophy tenets).
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {modalItemData.points.map((point, pIdx) => (
                      <div
                        key={pIdx}
                        style={{
                          padding: '12px',
                          background: '#ffffff',
                          borderRadius: '8px',
                          border: '1px solid #cbd5e1'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '800', color: '#334155' }}>
                            Point {pIdx + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleDeletePoint(pIdx)}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                            title="Remove Point"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                        <input
                          type="text"
                          className="unified-input"
                          style={{ marginBottom: '8px', padding: '8px 12px', fontSize: '13px' }}
                          value={point.title || ''}
                          onChange={(e) => handleModalPointChange(pIdx, 'title', e.target.value)}
                          placeholder="Point Title (e.g., Faith / Five-Leaved Tree)"
                        />
                        <textarea
                          className="unified-textarea"
                          rows="2"
                          style={{ padding: '8px 12px', fontSize: '13px' }}
                          value={point.description || ''}
                          onChange={(e) => handleModalPointChange(pIdx, 'description', e.target.value)}
                          placeholder="Point Description..."
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Media / Emblem Upload (Replaceable image, no delete button) */}
              <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Emblem / Illustration Image</label>
                {modalItemData.image && getMediaUrl(modalItemData.image) && (
                  <div style={{ width: '120px', height: '90px', borderRadius: '8px', overflow: 'hidden', marginBottom: '12px', background: '#fff', border: '1px solid #cbd5e1' }}>
                    <img
                      src={getMediaUrl(modalItemData.image)}
                      alt="Emblem preview"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </div>
                )}
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="file"
                    id="philosophy-image-upload"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                  />
                  <label htmlFor="philosophy-image-upload" className="unified-secondary-btn" style={{ cursor: 'pointer' }}>
                    <Upload size={14} /> {imageUploading ? `Uploading ${imageProgress}%...` : (modalItemData.image ? 'Replace Image' : 'Upload Image')}
                  </label>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={() => {
                  setEditingItemIndex(null);
                  setModalItemData(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSubmitItemModal}
                disabled={submitting[`item_${editingItemIndex}`] || !modalItemData.title || imageUploading}
              >
                <Save size={14} /> {submitting[`item_${editingItemIndex}`] ? 'Submitting...' : 'Save & Submit Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                cursor: 'pointer'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title || 'Philosophy image'}
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)', background: '#fff' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
